/**
 * Plex API node inspector (ADR-0085 Phase 2 §5.6).
 *
 * Mirrors `OperationInspectorProviderScope` (SQL provider) 1:1 — same tabs,
 * same parameter + binding modal pipeline, same cache/history widgets. The
 * only differences vs. SQL:
 *   - "SQL" tab → "Inputs" tab (JSON textarea + Run button)
 *   - Source picker is an Integration (not a Connector)
 *   - There's a Datasource ID field
 *
 * ADR-0129 — the 8 internal sections map onto the shared five-slot spine.
 * `plexInspectorTab` stays the single source of truth (the editor's cache-load
 * effect keys off `=== "cache"`); the outer spine slot is derived from it and
 * drives InspectorTabs in controlled mode. Multi-section slots (Setup, Data)
 * render their sections under an inner sub-tab bar.
 *
 * Substitution: the inputs JSON may carry `{:param_name}` tokens at value
 * positions; the server-side dispatcher (`execChainPlexApi`) resolves each
 * token against the parameter's binding (const / op variable / op window /
 * scope column / default) at runtime, preserving the bound value's JSON type.
 */

import { useMemo } from "react";
import type { Edge } from "@xyflow/react";
import { NODE_LABELS } from "../../lib/operationPorts.js";
import { TanTableViz } from "../TanTableViz.js";
import type { QueryColumnMeta, QueryResultsPayload } from "../QueryResultsGrid.js";
import { queryResultsToTanTableModel } from "../../lib/queryResultsToTanTable.js";
import { previewHeaderLabel } from "../../lib/previewHeader.js";
import { ColumnDomainSelect } from "../ColumnDomainControl.js";
import type { OperationCanvasNodeData, OperationCanvasNodeType } from "../OperationCanvasNode.js";
import {
  NodeMetadataFields,
  NodeOutputStats,
  NodeRunHistory,
  type NodeMetadata,
  type NodeOutputRecord,
  type NodeRunHistoryEntry
} from "./nodeInspectorWidgets.js";
import type { ToastKind } from "../Toast.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import type { InspectorSlot } from "../inspector/inspectorModel.js";

export type PlexInspectorTab =
  | "metadata"
  | "scope"
  | "inputs"
  | "parameters"
  | "bindings"
  | "schema"
  | "cache"
  | "history";

const PLEX_SLOT_OF: Record<PlexInspectorTab, InspectorSlot> = {
  metadata: "info",
  scope: "setup",
  inputs: "setup",
  parameters: "setup",
  bindings: "setup",
  schema: "data",
  cache: "data",
  history: "runs",
};

/** The sub-section a spine slot lands on when selected from the spine tab bar. */
function plexDefaultTabForSlot(slot: InspectorSlot): PlexInspectorTab {
  switch (slot) {
    case "setup":
      return "scope";
    case "data":
      return "schema";
    case "runs":
      return "history";
    case "info":
    default:
      return "metadata";
  }
}

type ProviderRef = { type: "shared"; provider_id: string } | { type: "local"; local_provider_id: string };

type BindingSource =
  | { type: "default" }
  | { type: "const"; value: string | number | boolean | null }
  | { type: "window"; window: string; field: "start_at" | "end_at" | "start_date" | "end_date" }
  | { type: "scope"; column: string }
  | { type: "variable"; variable: string };

type ProviderParamType = "string" | "int" | "decimal" | "bool" | "date" | "datetime";

type ProviderParameter = {
  name: string;
  type: ProviderParamType;
  required: boolean;
  default?: string | number | boolean | null;
  description?: string | null;
};

/** The Plex variant of the local-provider draft (analog of SQL's ProviderInspectorDraft). */
export type PlexInspectorDraft = {
  name: string;
  description?: string | null;
  integration_id?: string;
  source_spec: { datasource_id: number; inputs_template?: string };
  parameters: ProviderParameter[];
  schema_contract: { columns: QueryColumnMeta[] } | null;
};

type ProviderPreviewState = {
  loading: boolean;
  error: string | null;
  result: QueryResultsPayload | null;
};

type CanvasEdge = Edge<{ edge_type: "data" | "scope" }>;

export type OperationInspectorPlexApiProps = {
  selectedNode: OperationCanvasNodeType;
  operationMeta: NodeMetadata;
  inspectorTab: PlexInspectorTab;
  setInspectorTab: (tab: PlexInspectorTab) => void;
  selectedProviderRef: ProviderRef | null;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  /** Tenant Plex integrations available for assignment in local mode. */
  integrations: Array<{ id: string; name: string }>;
  edges: CanvasEdge[];
  nodes: OperationCanvasNodeType[];
  nodeOutputs: Record<string, NodeOutputRecord>;
  selectedProviderDraft: PlexInspectorDraft | null;
  updateMaterialProviderDraft: (
    nodeId: string,
    updater: (draft: PlexInspectorDraft) => PlexInspectorDraft
  ) => void | Promise<void>;
  selectedProviderPreview: ProviderPreviewState | null | undefined;
  executeSelectedPlexRequest: () => void | Promise<void>;
  /** ADR-0147 — Domain override control on the Schema tab (local plex nodes). */
  columnOverrides?: Record<string, string>;
  onColumnDomainChange?: (columnName: string, domain: import("../ColumnDomainControl.js").ColumnDomain | null) => void;
  retypeBusy?: boolean;
  openNewParameterModal: () => void;
  openEditParameterModal: (idx: number) => void;
  deleteParameterFromList: (idx: number) => void | Promise<void>;
  bindingDescription: (paramName: string, current: BindingSource) => string;
  openAddBindingModal: () => void;
  openBindingModal: (param: ProviderParameter) => void;
  clearBinding: (paramName: string) => void | Promise<void>;
  nodeCacheRefreshBusy: boolean;
  operationStatus: "draft" | "invalid" | "published" | "paused" | "validate";
  refreshProviderCache: (nodeId: string) => void | Promise<void>;
  nodeCacheLoading: Record<string, boolean>;
  nodeCache: Record<string, QueryResultsPayload | null>;
  nodeRunHistory: Record<string, NodeRunHistoryEntry[]>;
  nodeRunHistoryLoading: Record<string, boolean>;
  loadNodeRunHistory: (nodeId: string) => void;
  onSelectHistoryRun: (run: NodeRunHistoryEntry) => void;
  /** ADR-0128 — open a run's full-chain Trace from a History row. */
  onOpenTrace?: (runId: string) => void;
  pushToast: (t: { kind: ToastKind; message: string }) => void;
};

export function OperationInspectorPlexApi(props: OperationInspectorPlexApiProps): JSX.Element {
  const {
    selectedNode,
    operationMeta,
    inspectorTab,
    setInspectorTab,
    setNodeData,
    integrations,
    edges,
    nodes,
    nodeOutputs,
    selectedProviderDraft,
    updateMaterialProviderDraft,
    selectedProviderPreview,
    executeSelectedPlexRequest,
    columnOverrides,
    onColumnDomainChange,
    retypeBusy,
    openNewParameterModal,
    openEditParameterModal,
    deleteParameterFromList,
    bindingDescription,
    openAddBindingModal,
    openBindingModal,
    clearBinding,
    nodeCacheRefreshBusy,
    operationStatus,
    refreshProviderCache,
    nodeCacheLoading,
    nodeCache,
    nodeRunHistory,
    nodeRunHistoryLoading,
    onSelectHistoryRun,
    onOpenTrace,
    pushToast
  } = props;

  // ADR-0085 Phase 2 (revisited): Plex nodes are always local + always editable.
  // The `readOnly` flag (previously gated on shared mode) is retired.
  const readOnly = false;

  async function copyInputsJson() {
    const text = selectedProviderDraft?.source_spec.inputs_template ?? "";
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      pushToast({ kind: "success", message: "Inputs JSON copied" });
    } catch {
      pushToast({ kind: "error", message: "Could not copy to clipboard" });
    }
  }

  const previewTanModel = useMemo(
    () =>
      selectedProviderPreview?.result ? queryResultsToTanTableModel(selectedProviderPreview.result) : null,
    [selectedProviderPreview?.result]
  );

  const nodeCacheTanModel = useMemo(() => {
    const cached = nodeCache[selectedNode.id];
    return cached ? queryResultsToTanTableModel(cached) : null;
  }, [nodeCache, selectedNode.id]);

  const cachedRec = nodeOutputs[selectedNode.id];
  const cachedSummary = cachedRec?.runAt
    ? `Last run ${new Date(cachedRec.runAt).toLocaleString()}` +
      (typeof cachedRec.rowCount === "number" ? ` · ${cachedRec.rowCount.toLocaleString()} row${cachedRec.rowCount === 1 ? "" : "s"}` : "") +
      (typeof cachedRec.durationMs === "number" ? ` · ${cachedRec.durationMs} ms` : "") +
      (cachedRec.error ? ` · ${cachedRec.error}` : "")
    : "Never run";

  const setupSubTabs: Array<{ tab: PlexInspectorTab; label: string }> = [
    { tab: "scope", label: "Scope" },
    { tab: "inputs", label: "Inputs" },
    { tab: "parameters", label: "Parameters" },
    { tab: "bindings", label: "Bindings" }
  ];
  const dataSubTabs: Array<{ tab: PlexInspectorTab; label: string }> = [
    { tab: "schema", label: "Schema" },
    { tab: "cache", label: "Cache" }
  ];

  const draftMissingFallback = <div className="muted">Select or create a provider definition first.</div>;

  const renderSubTabBar = (label: string, items: Array<{ tab: PlexInspectorTab; label: string }>) => (
    <div className="reportTabs" role="tablist" aria-label={label}>
      {items.map((st) => (
        <button
          key={st.tab}
          className={`reportTabBtn${inspectorTab === st.tab ? " reportTabBtnActive" : ""}`}
          type="button"
          onClick={() => setInspectorTab(st.tab)}
        >
          {st.label}
        </button>
      ))}
    </div>
  );

  // ── Slot content ──────────────────────────────────────────────────────────

  const infoContent = (
    <>
      <div className="field">
        <label className="label">Node label</label>
        <input
          className="input"
          value={selectedNode.data.label}
          onChange={(e) => setNodeData(selectedNode.id, (data) => ({ ...data, label: e.target.value }))}
        />
      </div>
      {/* ADR-0085 Phase 2 (revisited 2026-05-26): Plex nodes are
          always local — the call shape (`inputs`) varies per use, so
          shared Plex providers offer no reuse benefit. Integrations are
          the shared layer (credentials + host); call config lives on the
          node. */}
      {selectedProviderDraft ? (
        <>
          <div className="field">
            <label className="label">Integration</label>
            <select
              className="input"
              value={selectedProviderDraft.integration_id ?? ""}
              onChange={(e) =>
                void updateMaterialProviderDraft(selectedNode.id, (draft) => ({
                  ...draft,
                  integration_id: e.target.value
                }))
              }
            >
              <option value="">— pick an integration —</option>
              {integrations.map((i) => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
            <div className="help">Manage tenant integrations at Tenant → Integrations.</div>
          </div>
          <div className="field">
            <label className="label">Datasource ID</label>
            <input
              className="input"
              type="number"
              value={selectedProviderDraft.source_spec.datasource_id || ""}
              onChange={(e) => {
                const n = Number(e.target.value);
                void updateMaterialProviderDraft(selectedNode.id, (draft) => ({
                  ...draft,
                  source_spec: {
                    ...draft.source_spec,
                    datasource_id: Number.isFinite(n) && n > 0 ? n : 0
                  }
                }));
              }}
              placeholder="1791"
            />
            <div className="help">Numeric Plex Data Source ID.</div>
          </div>
        </>
      ) : null}
      <div className="field">
        <label className="label">Description</label>
        <textarea
          className="textarea"
          rows={3}
          placeholder="Optional description for this node…"
          value={selectedNode.data.description ?? ""}
          onChange={(e) => setNodeData(selectedNode.id, (data) => ({ ...data, description: e.target.value }))}
        />
      </div>
      <NodeMetadataFields meta={operationMeta} />
    </>
  );

  const scopeSection =
    inspectorTab === "scope" ? (
      <div className="reportSection" style={{ display: "grid", gap: 10 }}>
        {(() => {
          const scopeEdge = edges.find((e) => e.target === selectedNode.id && e.data?.edge_type === "scope");
          if (!scopeEdge) {
            return (
              <>
                <div className="muted" style={{ fontWeight: 600 }}>Scope: None</div>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                  Connect another provider&apos;s tabular output to this node&apos;s scope port to enable per-row execution.
                  Then reference scope column names in parameter bindings via the Bindings tab.
                </p>
              </>
            );
          }
          const src = nodes.find((n) => n.id === scopeEdge.source);
          const schema = nodeOutputs[scopeEdge.source]?.outputSchema;
          return (
            <>
              <div className="muted" style={{ fontWeight: 600 }}>Scope: Connected</div>
              <div style={{ fontSize: 13 }}>
                Source:{" "}
                <strong>{src?.data.label?.trim() || (src ? NODE_LABELS[src.data.nodeType] : "Unknown")}</strong>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>Incoming scope variables</div>
              {!schema || schema.length === 0 ? (
                <div className="muted">Not available</div>
              ) : (
                <div className="tableWrap">
                  <table className="table tableCompact">
                    <thead>
                      <tr><th>Column</th><th>Type</th></tr>
                    </thead>
                    <tbody>
                      {schema.map((col) => (
                        <tr key={col.name}>
                          <td>{col.name}</td>
                          <td className="muted">{col.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                Each row from the scope source triggers a separate Plex call. Read-only.
              </p>
            </>
          );
        })()}
      </div>
    ) : null;

  const inputsSection =
    selectedProviderDraft && inspectorTab === "inputs" ? (
      <>
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">
            {readOnly ? "Inputs (read-only)" : "Inputs"}
          </h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn btnSm"
              type="button"
              onClick={() => void copyInputsJson()}
              disabled={!(selectedProviderDraft.source_spec.inputs_template ?? "").trim()}
            >
              Copy
            </button>
            <button
              className="btn btnSm"
              type="button"
              onClick={() => void executeSelectedPlexRequest()}
              disabled={selectedProviderPreview?.loading === true}
            >
              {selectedProviderPreview?.loading ? "Running..." : "Run"}
            </button>
          </div>
        </div>
        <textarea
          className="input"
          rows={12}
          style={{ fontFamily: "monospace", whiteSpace: "pre" }}
          readOnly={readOnly}
          value={selectedProviderDraft.source_spec.inputs_template ?? ""}
          onChange={(e) =>
            void updateMaterialProviderDraft(selectedNode.id, (draft) => ({
              ...draft,
              source_spec: { ...draft.source_spec, inputs_template: e.target.value }
            }))
          }
          placeholder={`{
  "inputs": {
    "Job_No": :job_param,
    "Workcenter_Key": :wc_param
  }
}`}
        />
        <div className="help">
          The complete JSON body POSTed to Plex — paste the documented example verbatim.
          Atlas does not wrap, unwrap, or transform this payload. Use{" "}
          <code>:param_name</code> tokens at value positions to substitute declared
          parameters at runtime — same convention as SQL provider templates. Substitution
          is type-preserving: int params emit as unquoted numbers, strings as JSON-quoted
          strings, bools as <code>true</code>/<code>false</code>. The template is not
          valid JSON until substituted.
        </div>
        {selectedProviderPreview?.error ? (
          <div className="muted" style={{ color: "var(--danger-700)" }}>
            {selectedProviderPreview.error}
          </div>
        ) : null}
        {previewTanModel ? (
          <div className="card" style={{ marginTop: 8, overflow: "hidden", minWidth: 0 }}>
            {previewHeaderLabel(selectedProviderPreview?.result) ? (
              <div className="muted" style={{ padding: "6px 8px" }}>{previewHeaderLabel(selectedProviderPreview?.result)}</div>
            ) : null}
            <TanTableViz model={previewTanModel} />
          </div>
        ) : null}
      </>
    ) : null;

  const parametersSection =
    selectedProviderDraft && inspectorTab === "parameters" ? (
      <>
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">
            Parameters{readOnly ? " (read-only)" : ""}
          </h3>
          {!readOnly ? (
            <button className="btn btnSm" type="button" onClick={openNewParameterModal}>
              Add parameter
            </button>
          ) : null}
        </div>
        {selectedProviderDraft.parameters.length === 0 ? (
          <div className="muted">No parameters declared.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                {!readOnly ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {selectedProviderDraft.parameters.map((param, idx) => (
                <tr key={`plex-param-${idx}`}>
                  <td>{param.name}</td>
                  <td>{param.type}</td>
                  <td>{param.required ? "Yes" : "No"}</td>
                  {!readOnly ? (
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <button className="linkBtn menuLinkAction" type="button" onClick={() => openEditParameterModal(idx)}>
                          Edit
                        </button>
                        <button
                          className="linkBtn menuLinkAction linkBtnDanger"
                          type="button"
                          onClick={() => void deleteParameterFromList(idx)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    ) : null;

  const bindingsSection =
    selectedProviderDraft && inspectorTab === "bindings" ? (
      <div className="reportSection">
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">Bindings</h3>
          {selectedProviderDraft.parameters.length > 0 && !readOnly ? (
            <button className="linkBtn menuLinkAction" type="button" onClick={openAddBindingModal}>
              Add binding
            </button>
          ) : null}
        </div>
        {selectedProviderDraft.parameters.length === 0 ? (
          <div className="muted">Declare parameters first, then bind them here.</div>
        ) : (
          (() => {
            const bindings =
              (selectedNode.data.config.bindings as Record<string, BindingSource> | undefined) ?? {};
            const entries = Object.entries(bindings).filter(([, current]) => current !== undefined);
            if (entries.length === 0) return <div className="muted">No bindings yet.</div>;
            return (
              <div style={{ display: "grid", gap: 8 }}>
                {entries.map(([paramName, current]) => {
                  const param = selectedProviderDraft!.parameters.find((entry) => entry.name === paramName);
                  if (!param) return null;
                  return (
                    <div
                      key={`plex-binding-${paramName}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        border: "1px solid var(--line)",
                        borderRadius: 8,
                        padding: "8px 10px"
                      }}
                    >
                      <div style={{ fontSize: 13, lineHeight: "18px" }}>
                        {bindingDescription(paramName, current)}
                      </div>
                      {!readOnly ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                          <button className="linkBtn menuLinkAction" type="button" onClick={() => openBindingModal(param)}>
                            Edit
                          </button>
                          <button
                            className="linkBtn menuLinkAction linkBtnDanger"
                            type="button"
                            onClick={() => void clearBinding(paramName)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}
      </div>
    ) : null;

  const schemaSection =
    selectedProviderDraft && inspectorTab === "schema" ? (
      <div className="reportSection">
        <h3 className="reportSectionTitle">Schema Contract</h3>
        {selectedProviderDraft.schema_contract?.columns && selectedProviderDraft.schema_contract.columns.length > 0 ? (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Column Name</th>
                  <th>Type</th>
                  <th>Domain</th>
                </tr>
              </thead>
              <tbody>
                {selectedProviderDraft.schema_contract.columns.map((column) => (
                  <tr key={column.name}>
                    <td>{column.name}</td>
                    <td>{column.rawType ?? column.type}</td>
                    <td>
                      {onColumnDomainChange ? (
                        <ColumnDomainSelect
                          columnName={column.name}
                          resolvedDomain={column.type}
                          override={columnOverrides?.[column.name]}
                          disabled={retypeBusy}
                          onChange={onColumnDomainChange}
                        />
                      ) : (
                        column.type
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="muted">No results yet — Run the request to populate the schema.</div>
        )}
      </div>
    ) : null;

  const cacheSection =
    selectedProviderDraft && inspectorTab === "cache" ? (
      <div className="reportSection" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="reportSectionHeader">
          <h3 className="reportSectionTitle">
            Cached data
            {nodeOutputs[selectedNode.id]?.rowCount != null
              ? ` — ${nodeOutputs[selectedNode.id].rowCount!.toLocaleString()} rows`
              : ""}
          </h3>
          <div className="toolbarGroup">
            <button
              className="btn btnSm"
              type="button"
              disabled={nodeCacheRefreshBusy || operationStatus === "validate"}
              onClick={() => void refreshProviderCache(selectedNode.id)}
              title={
                operationStatus === "validate" ? "Operation requires validation" : "Re-run the Plex call and update the cache"
              }
            >
              {nodeCacheRefreshBusy ? "Refreshing…" : "Refresh cache"}
            </button>
          </div>
        </div>

        <NodeOutputStats rec={nodeOutputs[selectedNode.id]} />

        {nodeCacheLoading[selectedNode.id] || !Object.prototype.hasOwnProperty.call(nodeCache, selectedNode.id) ? (
          <div className="muted">Loading cache…</div>
        ) : nodeCacheTanModel === null ? (
          <div className="muted">No cached data. Click &quot;Refresh cache&quot; to run the Plex call and populate the cache.</div>
        ) : (
          <>
            {previewHeaderLabel(nodeCache[selectedNode.id]) ? (
              <div className="muted" style={{ paddingBottom: 6 }}>{previewHeaderLabel(nodeCache[selectedNode.id])}</div>
            ) : null}
            <TanTableViz model={nodeCacheTanModel} />
          </>
        )}
      </div>
    ) : null;

  const historyContent = (
    <div className="reportSection" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <NodeRunHistory
        runs={nodeRunHistory[selectedNode.id] ?? []}
        loading={nodeRunHistoryLoading[selectedNode.id] ?? false}
        onSelectRun={onSelectHistoryRun}
        onOpenTrace={onOpenTrace}
      />
    </div>
  );

  // Setup sub-sections that require a loaded draft (Scope works without one).
  const setupNeedsDraft =
    inspectorTab === "inputs" || inspectorTab === "parameters" || inspectorTab === "bindings";

  return (
    <>
      <div className="muted" style={{ fontSize: 12, marginBottom: 6, flexShrink: 0 }}>{cachedSummary}</div>
      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="plex_api"
        activeSlot={PLEX_SLOT_OF[inspectorTab]}
        onSlotChange={(slot) => setInspectorTab(plexDefaultTabForSlot(slot))}
        tabs={[
          {
            slot: "setup",
            content: (
              <>
                {renderSubTabBar("Plex API setup sections", setupSubTabs)}
                {scopeSection}
                {inputsSection}
                {parametersSection}
                {bindingsSection}
                {!selectedProviderDraft && setupNeedsDraft ? draftMissingFallback : null}
              </>
            )
          },
          {
            slot: "data",
            content: (
              <>
                {renderSubTabBar("Plex API data sections", dataSubTabs)}
                {schemaSection}
                {cacheSection}
                {!selectedProviderDraft ? draftMissingFallback : null}
              </>
            )
          },
          {
            slot: "runs",
            content: historyContent
          },
          {
            slot: "info",
            content: infoContent
          }
        ]}
      />
    </>
  );
}
